export const BASE_HEALTH = 100
export const TANK_HEALTH_BOOST = 50
export const MAX_HEALTH_TANKS = 20

export const BASE_ENERGY = 100
export const TANK_ENERGY_BOOST = 50
export const MAX_ENERGY_TANKS = 20

export interface VitalityState {
  hp: number
  hpMax: number
  healthTanks: number
  energy: number
  energyMax: number
  energyTanks: number
  reserveTanks: number
  isAlive: boolean
}

export type VitalityChangeCallback = (state: VitalityState) => void
export type DamageCallback = (amount: number) => void
export type DeathCallback = () => void

export class PlayerVitality {
  private _state: VitalityState
  private changeCallbacks: VitalityChangeCallback[] = []
  private damageCallbacks: DamageCallback[] = []
  private deathCallbacks: DeathCallback[] = []

  constructor() {
    this._state = {
      hp: BASE_HEALTH,
      hpMax: BASE_HEALTH,
      healthTanks: 0,
      energy: BASE_ENERGY,
      energyMax: BASE_ENERGY,
      energyTanks: 0,
      reserveTanks: 0,
      isAlive: true,
    }
  }

  get state(): Readonly<VitalityState> {
    return this._state
  }

  onChange(cb: VitalityChangeCallback): () => void {
    this.changeCallbacks.push(cb)
    return () => {
      this.changeCallbacks = this.changeCallbacks.filter((c) => c !== cb)
    }
  }

  onDamage(cb: DamageCallback): () => void {
    this.damageCallbacks.push(cb)
    return () => {
      this.damageCallbacks = this.damageCallbacks.filter((c) => c !== cb)
    }
  }

  onDeath(cb: DeathCallback): () => void {
    this.deathCallbacks.push(cb)
    return () => {
      this.deathCallbacks = this.deathCallbacks.filter((c) => c !== cb)
    }
  }

  private notifyChange() {
    for (let i = 0; i < this.changeCallbacks.length; i++) {
      this.changeCallbacks[i](this._state)
    }
  }

  applyDamage(amount: number): number {
    if (!this._state.isAlive || amount <= 0) return 0

    const actualDamage = Math.min(this._state.hp, amount)
    this._state.hp -= actualDamage

    for (let i = 0; i < this.damageCallbacks.length; i++) {
      this.damageCallbacks[i](actualDamage)
    }

    if (this._state.hp <= 0) {
      if (this._state.reserveTanks > 0) {
        this._state.reserveTanks -= 1
        this._state.hp = this._state.hpMax
      } else {
        this._state.hp = 0
        this._state.isAlive = false
        for (let i = 0; i < this.deathCallbacks.length; i++) {
          this.deathCallbacks[i]()
        }
      }
    }

    this.notifyChange()
    return actualDamage
  }

  heal(amount: number): number {
    if (!this._state.isAlive || amount <= 0) return 0
    const healed = Math.min(this._state.hpMax - this._state.hp, amount)
    this._state.hp += healed
    if (healed > 0) this.notifyChange()
    return healed
  }

  addHealthTank(): boolean {
    if (this._state.healthTanks >= MAX_HEALTH_TANKS) return false
    this._state.healthTanks += 1
    this._state.hpMax += TANK_HEALTH_BOOST
    this._state.hp += TANK_HEALTH_BOOST
    this.notifyChange()
    return true
  }

  addEnergyTank(): boolean {
    if (this._state.energyTanks >= MAX_ENERGY_TANKS) return false
    this._state.energyTanks += 1
    this._state.energyMax += TANK_ENERGY_BOOST
    this._state.energy += TANK_ENERGY_BOOST
    this.notifyChange()
    return true
  }

  addReserveTank(): void {
    this._state.reserveTanks += 1
    this.notifyChange()
  }

  expendEnergy(amount: number): boolean {
    if (amount <= 0) return true
    if (this._state.energy < amount) return false
    this._state.energy -= amount
    this.notifyChange()
    return true
  }

  rechargeEnergy(amount: number): number {
    if (amount <= 0) return 0
    const recharged = Math.min(this._state.energyMax - this._state.energy, amount)
    this._state.energy += recharged
    if (recharged > 0) this.notifyChange()
    return recharged
  }
}
